import { useNavigate } from 'react-router-dom'
import icons from '../../config/icons.config'
import type { ProgressType } from '../../model/Challenge'
import type { ResolvedCheckpoint } from '../../logic/checkpointUtils'
import type { RouteCoordinate } from '../../logic/routeUtils'
import { ScreenPath } from '../../navigation'
import StringUtil from '../../util/StringUtil'
import AppIcon from '../icons/AppIcon'
import ProgressBar from '../primitives/ProgressBar'
import ProgressGraphic from '../progress/ProgressGraphic'
import RmInfo from './RmInfo'
import styles from './ProgressHero.module.css'

interface ProgressHeroProps {
  currentDay: number
  totalDays: number
  teamProgress: number
  goal: number
  progressPercent: number
  paceProgress: number
  paceDelta: number
  dailyPace: number
  progressType: ProgressType
  checkpoints: ResolvedCheckpoint[]
  currentCheckpointId?: string
  routeCoordinates: RouteCoordinate[]
  gateMarks: { position: number; status: 'done' | 'next' | 'pending' }[]
}

export default function ProgressHero({
  currentDay,
  totalDays,
  teamProgress,
  goal,
  progressPercent,
  paceProgress,
  paceDelta,
  dailyPace,
  progressType,
  checkpoints,
  currentCheckpointId,
  routeCoordinates,
  gateMarks,
}: ProgressHeroProps) {
  const navigate = useNavigate()
  const ahead = paceDelta >= 0
  const mapEnabled = progressType === 'map'

  function handleClick() {
    if (mapEnabled) navigate(ScreenPath.map)
  }

  return (
    <section
      className={[styles.hero, mapEnabled && styles.clickable].filter(Boolean).join(' ')}
      data-testid="progress-hero"
      onClick={handleClick}
      role={mapEnabled ? 'button' : undefined}
      tabIndex={mapEnabled ? 0 : undefined}
      onKeyDown={
        mapEnabled
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                navigate(ScreenPath.map)
              }
            }
          : undefined
      }
    >
      <div className={styles.metaRow}>
        <div className={styles.dayLabel}>
          DAY <b>{currentDay}</b> / {totalDays}
        </div>
        <div onClick={(event) => event.stopPropagation()}>
          <RmInfo />
        </div>
      </div>

      <div className={styles.bigNum}>
        <span className={styles.val} data-testid="team-progress">
          {StringUtil.numberFormat(teamProgress)}
        </span>
        <span className={styles.suffix}>
          / {StringUtil.numberFormat(goal)} <span className={styles.unit}>RMs</span>
        </span>
      </div>

      {mapEnabled && (
        <div className={styles.oa}>
          <ProgressBar percent={progressPercent} size="lg" />
          {gateMarks.map((mark, i) => (
            <i
              key={i}
              className={[
                styles.gateMark,
                mark.status === 'done' && styles.gateMarkDone,
                mark.status === 'next' && styles.gateMarkNext,
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ left: `${mark.position}%` }}
            />
          ))}
          <span className={styles.finFlag}>
            <AppIcon icon={icons.trophy} size={14} />
          </span>
        </div>
      )}

      <div onClick={(event) => event.stopPropagation()}>
        <ProgressGraphic
          progressType={progressType}
          teamProgress={teamProgress}
          paceProgress={paceProgress}
          goal={goal}
          checkpoints={checkpoints}
          currentCheckpointId={currentCheckpointId}
          routeCoordinates={routeCoordinates}
        />
      </div>

      <div className={styles.paceRow}>
        <span className={[styles.paceText, !ahead && styles.negative].filter(Boolean).join(' ')}>
          <b>
            {ahead ? '+' : ''}
            {Math.abs(paceDelta)} RMs {ahead ? 'ahead of' : 'behind'} pace
          </b>{' '}
          ({dailyPace} RMs/day)
        </span>
      </div>
    </section>
  )
}
